import React, { useState } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import { AdminUser } from '../../../../API/AdminInfo/models'
import { SingleButton } from '../../../../API/Buttons/models'
import { createBulkVariants } from '../../../../API/Buttons/Buttons'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'
import { FaCheck } from 'react-icons/fa'

interface Props {
    show: boolean
    onHide: () => void
    selectedButton: SingleButton | null
    adminInfo: AdminUser | null
}

export const BulkUploadVariantsModal = ({ show, onHide, selectedButton, adminInfo }: Props) => {
    const [file, setFile] = useState<File | null>(null)
    const [loading, setLoading] = useState(false)
    const [previewVariants, setPreviewVariants] = useState<string[]>([])
    const [showPreview, setShowPreview] = useState(false)

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (selectedFile) {
            setFile(selectedFile)
            processExcelFile(selectedFile)
        }
    }

    const processExcelFile = (file: File) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer)
                const workbook = XLSX.read(data, { type: 'array' })
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

                // Extraer solo los textos de la primera columna y filtrar vacíos
                const variants = jsonData
                    .map((row: any) => row[0])
                    .filter((text: any) => text && typeof text === 'string' && text.trim() !== '')
                    .map((text: string) => text.trim())

                setPreviewVariants(variants)
                setShowPreview(true)
            } catch (error) {
                toast.error('Error al procesar el archivo Excel')
                console.error('Error processing Excel file:', error)
            }
        }
        reader.readAsArrayBuffer(file)
    }

    const handleUpload = async () => {
        if (!selectedButton || previewVariants.length === 0) {
            toast.error('No hay variantes para subir')
            return
        }

        setLoading(true)
        try {
            const result = await createBulkVariants({
                buttonId: selectedButton.id,
                variants: previewVariants
            })

            if (result && result?.success) {
                toast.success(result?.msg)
                handleClose()
            } else {
                toast.error(result?.msg || 'Error al crear las variantes')
            }
        } catch (error) {
            toast.error('Error al crear las variantes')
            console.error('Error uploading variants:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setFile(null)
        setPreviewVariants([])
        setShowPreview(false)
        onHide()
    }

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Carga Masiva de Variantes</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <p className="text-white">
                        Sube un archivo Excel (.xlsx) donde cada fila de la primera columna contenga el texto de una variante.
                    </p>
                    <Alert variant="info" className="small">
                        <strong>Formato esperado:</strong><br />
                        • Primera columna: Texto de la variante<br />
                        • Una variante por fila<br />
                        • Las filas vacías serán ignoradas
                    </Alert>
                </div>

                <Form.Group className="mb-3">
                    <Form.Label>Seleccionar archivo Excel</Form.Label>
                    <Form.Control
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                    />
                </Form.Group>

                {showPreview &&
                    <div className='d-flex align-items-center gap-2 text-success'>
                        <FaCheck /> {previewVariants.length} variantes encontradas
                    </div>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={!showPreview || previewVariants.length === 0 || loading}
                >
                    {loading ? 'Subiendo...' : `Crear ${previewVariants.length} Variantes`}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
